import React from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import SiteLogo from "./SiteLogo";
import FooterSocialLinks from "./FooterSocialLinks";

export default function Footer() {
  const { t, contactInfo } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden border-t border-[#fbd9e5]/10 bg-[#3e2723] text-[#fbd9e5]" data-testid="footer">
      <div aria-hidden className="pointer-events-none absolute -top-24 right-0 h-48 w-48 rounded-full bg-[#fbd9e5]/8 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 py-10 sm:px-10 lg:px-16 lg:py-12">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8 lg:items-start">
          {/* Brand + tagline + social */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex max-w-full items-center">
              <SiteLogo variant="footer" />
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#fbd9e5]/72 line-clamp-3">{t.footer.tagline}</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <a
                href={`mailto:${contactInfo.email}`}
                className="inline-flex items-center gap-2 rounded-full border border-[#fbd9e5]/25 px-3 py-1.5 text-xs font-medium text-[#fbd9e5]/90 transition-colors hover:bg-[#fbd9e5]/10"
                data-testid="footer-mail"
              >
                <Mail className="h-3.5 w-3.5 shrink-0 opacity-80" />
                {contactInfo.email}
              </a>
            </div>
            <FooterSocialLinks contactInfo={contactInfo} />
          </div>

          {/* Navigation */}
          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-5 lg:grid-cols-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#fbd9e5]/45">{t.footer.explore}</p>
              <ul className="mt-3 space-y-2 text-sm text-[#fbd9e5]/85">
                <li>
                  <Link to="/" className="transition-colors hover:text-white">
                    {t.nav.home}
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="transition-colors hover:text-white">
                    {t.nav.services}
                  </Link>
                </li>
                <li>
                  <Link to="/portfolio" className="transition-colors hover:text-white">
                    {t.nav.portfolio}
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="transition-colors hover:text-white">
                    {t.nav.about}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="transition-colors hover:text-white">
                    {t.nav.contact}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#fbd9e5]/45">{t.footer.services_title}</p>
              <ul className="mt-3 space-y-2 text-sm text-[#fbd9e5]/75">
                {t.footer.services_list.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#fbd9e5]/45">{t.footer.contact_title}</p>
            <ul className="mt-3 space-y-3 text-sm">
              <li>
                <a href={`mailto:${contactInfo.email}`} className="inline-flex items-center gap-1 text-[#fbd9e5]/90 hover:text-white">
                  {contactInfo.email}
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-70" />
                </a>
              </li>
              <li className="text-[#fbd9e5]/75">{contactInfo.phone}</li>
              <li className="text-[#fbd9e5]/65 text-xs leading-relaxed">{t.contact.location_value}</li>
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-2 border-t border-[#fbd9e5]/10 pt-6 text-[11px] text-[#fbd9e5]/45 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {year} Social Destiny · {t.footer.rights}
          </p>
          <p className="sm:text-right">{t.footer.made_with}</p>
        </div>
      </div>
    </footer>
  );
}
